import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Loading from "../../../ui-components/loading/loading";
import ThreeDotsMenu from "../../../ui-components/three-dots-menu/threeDotsMenu";
import Button, { ButtonSizes, ButtonThemes } from "../../../ui-components/button/button";
import {
  getPickupLog,
} from "../../../slices/pickup/pickupDetailsSlice";
import { userSelector } from "../../../slices/user/userSlice";
import { PickupStatus } from "../../../app/data/common/route";
import boxIcon from "../../../images/box.svg";
import buildingIcon from "../../../images/building.svg";
import calendarIcon from "../../../images/calendar.svg";
import checkIcon from "../../../images/check_gray.svg";
import check2Icon from "../../../images/check_gray-2.svg";
import crossRedIcon from "../../../images/cross_red.svg";
import crossGrayIcon from "../../../images/cross_gray.svg";
import emailIcon from "../../../images/letter_gray.svg";
import imagePreview from "../../../images/image-preview.png";
import newWindow from "../../../images/new-window_gray.svg";
import personIcon from "../../../images/person_blue.svg";
import phoneIcon from "../../../images/phone_gray.svg";
import pointIcon from "../../../images/point.svg";
import clockIcon from "../../../images/clock_blue.svg";
import calendarDotsIcon from "../../../images/calendar-dots_blue.svg";
import { ReactComponent as InfoIcon } from "../../../images/info.svg";
import { ReactComponent as LinkIcon } from "../../../images/link.svg";
import PickupDetailsBlock from "./pickupDetailsBlock";
import { PickupFreightItemsBlock } from "./pickupFreightItemsBlock";
import PickupDetailsLog from "./pickupDetailsLog";
import PickupReschedule from "./pickupReschedule";
import PickupCancel from "./pickupCancel";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { getStatusColor, getCustomerPickupSourceLabel, isAiPickupServiceCreatedBy, isEmailSourcedPickup } from "../../../services/pickups";
import PickupComplete from "./pickupComplete";
import Tag from "../../../ui-components/molecules/tag/tag";
import { Popover } from "../../../ui-components/popover/popover";
import { TagColor } from "../../../app/data/common/tagColor";
import { PickupDetailsModel, UpdatePickupModel } from "../../../app/data/pickup/models";
import { cn } from "../../../services/common/className";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import PickupRequestsService from "../../../app/data/pickup-requests/pickupRequestsService";
import { INBOX_STATIC_QUERY_OPTIONS } from "../../pickup/inbox/utils/pickupInboxQueryOptions";
import EmailViewerSidebar from "../email-viewer/EmailViewerSidebar";

import "./pickupDetails.scss";

const pickupRequestsService = PickupRequestsService.getInstance();

const AI_CREATED_BY_TOOLTIP =
  "Pickup was created by XGS AI based on the information received in email from customer";

export interface PickupDetailsProps {
  pickup: PickupDetailsModel | null;
  onClose: () => void;
  showReschedule?: boolean;
  onUpdatePickup?: (pickup: UpdatePickupModel) => void;
  onReschedulePickup?: (pickup: UpdatePickupModel) => void;
  isDriverLoading?: boolean;
  isOpen: boolean;
  isLoading?: boolean;
  readonly?: boolean;
  onShowPickup?: (pickupNumber: string, pickupDate?: string) => void;
  error?: string;
};

const formatRequesterDisplay = (requester: unknown): string | undefined => {
  if (!requester) return undefined;
  if (typeof requester === "string") return requester;
  if (typeof requester === "object" && requester !== null) {
    const { teamName, teamId } = requester as { teamName?: string; teamId?: string };
    return teamName || teamId;
  }
  return undefined;
};

const PickupDetails: React.FC<PickupDetailsProps> = ({ isOpen, isLoading, pickup, isDriverLoading, onClose, onUpdatePickup, onReschedulePickup, showReschedule, readonly, onShowPickup, error }) => {
  const dispatch = useDispatch();
  const userState = useSelector(userSelector);
  const [showReschedulePickup, setShowReschedulePickup] = useState(false);
  const [showCancelPickup, setShowCancelPickup] = useState(false);
  const [showCompletePickup, setShowCompletePickup] = useState(false);
  const [showEmailViewer, setShowEmailViewer] = useState(false);
  const [rescheduleEntry, setRescheduleEntry] = useState<{pickupNumber: string, pickupDate: string, actionPerformed: string} | null>(null);

  const isEmailSource = isEmailSourcedPickup(pickup?.creatorType);
  const { data: pickupEmailData } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_REQUEST_EMAIL_BY_PICKUP_NUMBER, pickup?.pickupNumber],
    queryFn: () => pickupRequestsService.getEmailByOneXgsPickupNumber(pickup!.pickupNumber),
    enabled: isOpen && !!pickup?.pickupNumber && isEmailSource,
    retry: false,
    ...INBOX_STATIC_QUERY_OPTIONS,
  });

  const canViewPickupEmail =
    pickupEmailData?.mimeAvailable === true && pickupEmailData.inboundMessageId != null;

  const pickupSourceLabel = useMemo(() => {
    if (!pickup?.creatorType) return undefined;
    const label = getCustomerPickupSourceLabel(pickup.creatorType);
    return label !== "-" ? label : pickup.creatorType;
  }, [pickup?.creatorType]);

  const pickupSourceValue = useMemo(() => {
    if (!pickupSourceLabel) return undefined;
    if (!canViewPickupEmail) return pickupSourceLabel;

    return (
      <button
        type="button"
        className="xgs-pickup-details__email-source-link"
        onClick={() => setShowEmailViewer(true)}
      >
        {pickupSourceLabel}
        <LinkIcon />
      </button>
    );
  }, [canViewPickupEmail, pickupSourceLabel]);

  const createdByValue = useMemo(() => {
    const emailSender = pickupEmailData?.email?.sender?.trim();
    if (
      isEmailSource &&
      emailSender &&
      isAiPickupServiceCreatedBy(pickup?.userCreated)
    ) {
      return (
        <span className="xgs-pickup-details__created-by">
          <span className="xgs-pickup-details__created-by__email">{emailSender}</span>
          <Popover
            trigger="hover"
            values={
              <div className="xgs-pickup-details__created-by__tooltip">{AI_CREATED_BY_TOOLTIP}</div>
            }
          >
            <Tag mods={{ color: TagColor.BLUE, uppercase: false }} mix="xgs-pickup-details__created-by__tag">
              AI
            </Tag>
          </Popover>
        </span>
      );
    }

    return pickup?.userCreated;
  }, [isEmailSource, pickup?.userCreated, pickupEmailData?.email?.sender]);

  const isAssignedPickup = () => {
    return pickup?.pickupStatus === PickupStatus.ASSIGNED;
  };

  const isClosedPickup = () => {
    return (pickup?.pickupStatus === PickupStatus.COMPLETED) || (pickup?.pickupStatus === PickupStatus.CANCELED);
  };

  const isTodayPickup = () => {
    return moment(pickup?.pickupDate).startOf("day").isSame(moment().startOf("day"));
  };

  const pickupTimeHasCome = () => {
    return !(moment(pickup?.pickupDate).startOf("day").isAfter(moment().startOf("day")));
  };

  const isAtRisk = () => {
    if (isClosedPickup()) return false;
    if (isTodayPickup()) {
      return moment().isAfter(moment(pickup?.pickupCloseTime, "hh:mm:ss").subtract(3, "hours"))
        && moment().isBefore(moment(pickup?.pickupCloseTime, "hh:mm:ss"));
    } else {
      return false;
    }
  };

  const isExpired = () => {
    if (isClosedPickup()) return false;
    let result;
    if (isTodayPickup()) {
      result = moment().isAfter(moment(pickup?.pickupCloseTime, "hh:mm:ss"));
    } else if (moment(pickup?.pickupDate).startOf("day").isBefore(moment().startOf("day"))) {
      result = true;
    } else {
      result = false;
    }
    return result;
  };

  const addRescheduleEntry = useCallback((entry) => {setRescheduleEntry(entry)}, []);

  useEffect(() => {
    setShowCancelPickup(false);
    setShowReschedulePickup(false);
    setShowCompletePickup(false);
    setShowEmailViewer(false);
    setRescheduleEntry(null);
  }, [pickup?.pickupNumber]);

  useEffect(() => {
    if (!showReschedule) return;
    setShowReschedulePickup(true);
  }, [showReschedule]);

  return (
    <SlideOutSidebar
      header="Pickup Details"
      onClose={onClose}
      show={isOpen}
      className="xgs-slide-out-sidebar--stack-above-page-modals"
    >
      <div className="xgs-pickup-details">
        {isLoading && (<div>
          <Loading isLoading />
        </div>
        )}

        {!!error && (<div>
          <XGSErrorMessage>{error}</XGSErrorMessage>
        </div>
        )}

        {pickup && !isLoading && !error && (
          <>
            <div className="xgs-pickup-details__header">
              <div>
                <div className="xgs-pickup-details__pickup-number">Pickup: {pickup.pickupNumber}</div>
                {pickup.probillNumber && (
                  <div className="xgs-pickup-details__probill-number">
                    <div className="xgs-pickup-details__probill-number__text">Probill: {pickup.probillNumber}</div>
                    <div className="xgs-pickup-details__probill-number__link">
                      <a href={`/shipments/${pickup.probillNumber}`} target="_blank" rel="noreferrer">
                        <img src={newWindow} alt="open in new window" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
              {pickup?.pickupStatus && <div className="xgs-pickup-details__status">Current Status: <Tag mods={{ color: getStatusColor(pickup?.pickupStatus) }}>{pickup?.pickupStatus}</Tag></div>}
            </div>
            <div className="xgs-pickup-details__subheader">
              <div className="xgs-pickup-details__pickup-date">
                {isAtRisk() && <Tag mods={{ color: TagColor.ORANGE, uppercase: true }} mix="xgs-pickup-details__pickup-date__label">AT RISK</Tag>}
                {isExpired() && !pickup.missed && <Tag mods={{ color: TagColor.RED, uppercase: true }} mix="xgs-pickup-details__pickup-date__label">EXPIRED</Tag>}
                {pickup.missed && <div className="xgs-pickup-details__pickup-date__label">MISSED</div>}
                <div className="xgs-pickup-details__pickup-date__icon">
                  <img src={calendarIcon} alt="calendar" />
                </div>
                <div className="xgs-pickup-details__pickup-date__value">
                  {pickup?.pickupDate?.toUiDateFormat()} {moment(pickup?.pickupReadyTime, "hh:mm:ss").format("h:mm A")} - {moment(pickup?.pickupCloseTime, "hh:mm:ss").format("h:mm A")}
                </div>
              </div>
              {!readonly && (<div className="xgs-pickup-details__actions">                
                {!isClosedPickup() && (
                  <>
                    <div
                      className="xgs-pickup-details__action"
                      onClick={() => setShowReschedulePickup(true)}
                    >
                      <div className="xgs-pickup-details__action__icon">
                        <img src={calendarDotsIcon} alt="reschedule" />
                      </div>
                      <div className="xgs-pickup-details__action__text">
                        Reschedule
                      </div>
                    </div>
                    <ThreeDotsMenu
                      menuItems={[
                        ...((isAssignedPickup() && pickupTimeHasCome())
                          ? [{
                            text: "Complete Pickup",
                            icon: <img src={check2Icon} alt="complete" />,
                            onClick: () => setShowCompletePickup(true)
                          }]
                          : []),
                        {
                          text: "Cancel Pickup",
                          icon: <img src={crossGrayIcon} alt="cancel" />,
                          onClick: () => setShowCancelPickup(true)
                        }
                      ]}
                    />
                  </>
                )}
              </div>
              )}
            </div>
            {!!rescheduleEntry && (
              <div className={cn("xgs-pickup-details__rescheduled")({ color: rescheduleEntry.actionPerformed === "RESCHEDULED_TO" ? "red" : "blue" })}>
                <div className="xgs-pickup-details__rescheduled__header"><InfoIcon width={20} height={20}/> Pickup Rescheduled</div>
                <div className="xgs-pickup-details__rescheduled__text">
                  {rescheduleEntry.actionPerformed === "RESCHEDULED_TO" && <>
                    This Pickup was rescheduled to new one. New pickup number is {rescheduleEntry.pickupNumber}.<br />
                    The customer should have received an email notifying this.
                  </>}

                  {rescheduleEntry.actionPerformed === "RESCHEDULE_PICKUP" && <>
                    The pickup has been rescheduled to a new date.
                  </>}
                </div>

                {onShowPickup 
                && (moment(rescheduleEntry.pickupDate).startOf("day").isSame(moment().startOf("day")) || moment(rescheduleEntry.pickupDate).startOf("day").isBefore(moment().startOf("day"))) 
                && <Button
                  className="xgs-pickup-details__rescheduled__button"
                  theme={rescheduleEntry.actionPerformed === "RESCHEDULED_TO" ? ButtonThemes.red : ButtonThemes.blue}
                  size={ButtonSizes.small}
                  onClick={() => onShowPickup?.(rescheduleEntry.pickupNumber)}
                >
                  {rescheduleEntry.actionPerformed === "RESCHEDULED_TO" && <>Show New Pickup&nbsp;<LinkIcon/></>}
                  {rescheduleEntry.actionPerformed === "RESCHEDULE_PICKUP" && <>Show in Table&nbsp;<LinkIcon/></>}
                </Button>
                }
              </div>
            )}
            {(() => {
              const requesterDisplay = formatRequesterDisplay(pickup.requester);
              return requesterDisplay ? (
                <div className="xgs-pickup-details__section">
                  <div className="xgs-pickup-details__block__content__pair">
                    <div className="xgs-pickup-details__block__content__label">
                      Requester Company:
                    </div>
                    <div className="xgs-pickup-details__block__content__value">
                      {requesterDisplay}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
            <div className="xgs-pickup-details__section">
              <div className="xgs-pickup-details__section__line">
                <div className="xgs-pickup-details__section__icon">
                  <img src={buildingIcon} alt="building" />
                </div>
                <div className="xgs-pickup-details__section__header">
                  Pickup From
                </div>
              </div>
              <div className="xgs-pickup-details__section__content">
                <div className="xgs-pickup-details__section__content__line">
                  <PickupDetailsBlock
                    header="Shipper Location"
                    content={[
                      {
                        label: "Company",
                        value: pickup.shipper.name,
                        required: true,
                      },
                      {
                        label: "Address",
                        value: (pickup.shipper.address.address1 ? pickup.shipper.address.address1.toLowerCase().capitalize() + ", " : "") + (pickup.shipper.address.city ? pickup.shipper.address.city.toLowerCase().capitalize() + ", " : "") + (pickup.shipper.address.state ? pickup.shipper.address.state + ", " : "") + pickup.shipper.address.postalCode,
                        required: true,
                      },
                      {
                        label: "Additional Address Line",
                        value: pickup.shipper.address.additionalAddressLine
                      },
                    ]}
                    oneLine
                  />
                </div>
                <div className="xgs-pickup-details__section__content__line">
                  <PickupDetailsBlock
                    header="Shipper Contact"
                    content={[
                      ...((pickup.shipper.firstName || pickup.shipper.lastName) ? [
                        {
                          label: <div className="xgs-pickup-details__value-icon"><img src={personIcon} alt="person" /></div>,
                          value: (pickup.shipper.firstName || "") + " " + (pickup.shipper.lastName || "")
                        }
                      ] : []),
                      {
                        label: <div className="xgs-pickup-details__value-icon"><img src={phoneIcon} alt="phone" /></div>,
                        value: pickup.shipper.mobilePhone === "0" ? null : pickup.shipper.mobilePhone
                      },
                      ...((pickup.shipper.allowSms !== null) ? [
                        {
                          label: pickup.shipper.allowSms
                            ? <div className="xgs-pickup-details__value-icon"><img src={checkIcon} alt="sms yes" /></div>
                            : <div className="xgs-pickup-details__value-icon"><img src={crossRedIcon} alt="sms no" /></div>,
                          value: pickup.shipper.allowSms ? "SMS allowed" : "SMS not allowed"
                        }
                      ] : []),
                      {
                        label: <div className="xgs-pickup-details__value-icon"><img src={emailIcon} alt="email" /></div>,
                        value: pickup.shipper.email,
                        required: true,
                      },
                    ]}
                    oneLine
                  />
                </div>
              </div>
            </div>
            <div className="xgs-pickup-details__section">
              <div className="xgs-pickup-details__section__line">
                <div className="xgs-pickup-details__section__icon">
                  <img src={pointIcon} alt="point" />
                </div>
                <div className="xgs-pickup-details__section__header">
                  Deliver To
                </div>
              </div>
              <div className="xgs-pickup-details__section__content">
                <PickupDetailsBlock
                  header="Consignee Details"
                  content={[
                    {
                      label: "Company",
                      value: pickup.consignee.name,
                      required: true
                    },
                    {
                      label: "Address",
                      value: (pickup.consignee.address.address1 ? pickup.consignee.address.address1.toLowerCase().capitalize() + ", " : "") + (pickup.consignee.address.city ? pickup.consignee.address.city.toLowerCase().capitalize() + ", " : "") + (pickup.consignee.address.state ? pickup.consignee.address.state + ", " : "") + pickup.consignee.address.postalCode,
                      required: true
                    },
                    {
                      label: "Additional Address Line",
                      value: pickup.consignee.address.additionalAddressLine
                    },
                    {
                      label: "Phone",
                      value: pickup.consignee.phone,
                      required: true
                    },
                    ...(pickup.consignee.email ? [
                      {
                        label: "Email",
                        value: (<a href={`mailto:${pickup.consignee.email}`}>{pickup.consignee.email}</a>)
                      }
                    ] : [])
                  ]}
                  oneLine
                />
              </div>
            </div>
            <div className="xgs-pickup-details__section">
              <div className="xgs-pickup-details__section__line">
                <div className="xgs-pickup-details__section__icon">
                  <img src={calendarIcon} alt="calendar" />
                </div>
                <div className="xgs-pickup-details__section__header">
                  Pickup Info
                </div>
              </div>
              <div className="xgs-pickup-details__section__content">
                <div className="xgs-pickup-details__grid">
                  <div className="xgs-pickup-details__grid__item">
                    <PickupDetailsBlock
                      header="Pickup Details"
                      content={[
                        {
                          label: "Pickup Date",
                          value: (
                            <span style={{ color: (!isTodayPickup() && (isExpired() || isAtRisk())) ? "#C43232" : "inherit" }}>
                              {pickup?.pickupDate?.toUiDateFormat()}
                            </span>
                          )
                        },
                        {
                          label: "Pickup Time",
                          value: (
                            <span style={{ color: (isExpired() || isAtRisk()) ? "#C43232" : "inherit" }}>
                              {moment(pickup?.pickupReadyTime, "hh:mm:ss").format("h:mm A") + " -  " + moment(pickup?.pickupCloseTime, "hh:mm:ss").format("h:mm A")}
                            </span>
                          )
                        },
                        ...pickup.driverId ? [{
                          label: "Assigned Driver",
                          value: (
                            <>
                              {isDriverLoading && (
                                <div className="xgs-pickup-details__value-spinner">
                                  <Loading isLoading />
                                </div>
                              )}
                              {pickup.driverName
                                ? (<>{pickup.driverName} <span>({pickup.driverId})</span></>)
                                : pickup.driverId}
                            </>
                          )
                        }] : []
                      ]}
                    />
                  </div>
                  <div className="xgs-pickup-details__grid__item">
                    <PickupDetailsBlock
                      header="Request Details"
                      content={[
                        {
                          label: "Pickup Created",
                          value: pickup.dateCreated?.toUiDateTimeShortFormat()
                        },
                        {
                          label: "Created By",
                          value: createdByValue
                        },
                        {
                          label: "Source",
                          value: pickupSourceValue
                        },
                        {
                          label: "Last Status Update",
                          value: pickup.dateUpdated?.toUiDateTimeShortFormat()
                        },
                        {
                          label: "Updated By",
                          value: pickup.userUpdated
                        }
                      ]}
                    />
                  </div>
                  {pickup.payor?.name && (
                    <div className="xgs-pickup-details__grid__item">
                      <PickupDetailsBlock
                        header="Payor Details"
                        content={[
                          {
                            label: "Company",
                            value: pickup.payor.name,
                            required: true,
                          },
                          {
                            label: "Address",
                            value: pickup.payor.address
                              ? [
                                  pickup.payor.address.address1,
                                  pickup.payor.address.city,
                                  pickup.payor.address.state,
                                  pickup.payor.address.postalCode,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : undefined,
                            required: true,
                          },
                          {
                            label: "Phone",
                            value: pickup.payor.phone,
                            required: true,
                          }
                        ]}
                      />
                    </div>
                  )}
                  {(pickup.bolNumber || pickup.poNumber || pickup.additionalReference) && (
                    <div className="xgs-pickup-details__grid__item">
                      <PickupDetailsBlock
                        header="Additional Information"
                        content={[
                          {
                            label: "BOL #",
                            value: pickup.bolNumber
                          },
                          {
                            label: "PO #",
                            value: pickup.poNumber
                          },
                          {
                            label: "Add. Ref.",
                            value: pickup.additionalReference
                          }
                        ]}
                      />
                    </div>
                  )}
                  {pickup.specialInstructions && (
                    <div className="xgs-pickup-details__grid__item">
                      <PickupDetailsBlock
                        header="Special Instructions"
                        content={[
                          {
                            value: pickup.specialInstructions
                          }
                        ]}
                      />
                    </div>
                  )}
                  {(pickup.documents && pickup.documents.length > 0) && (
                    <div className="xgs-pickup-details__grid__item">
                      <PickupDetailsBlock
                        header="Documents"
                        content={pickup.documents.map((item) => {
                          return {
                            value: (
                              <div className="xgs-pickup-details__document">
                                <a href={item.storageUrl} target="_blank" rel="noreferrer">
                                  <img src={imagePreview} alt="preview" />
                                  {item.type === "BOL_PHOTO" ? "BOL" : "Misc"}
                                </a>
                              </div>
                            )
                          }
                        })}
                        oneLine
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="xgs-pickup-details__section">
              <div className="xgs-pickup-details__section__line">
                <div className="xgs-pickup-details__section__icon">
                  <img src={boxIcon} alt="box" />
                </div>
                <div className="xgs-pickup-details__section__header">
                  Freight Details
                </div>
              </div>
              <div className="xgs-pickup-details__section__content">
                <PickupFreightItemsBlock items={pickup.items} />
              </div>
            </div>
            <div className="xgs-pickup-details__section">
              <div className="xgs-pickup-details__section__line">
                <div className="xgs-pickup-details__section__icon">
                  <img src={clockIcon} alt="clock" />
                </div>
                <div className="xgs-pickup-details__section__header">
                  Activity History
                </div>
              </div>
              <div className="xgs-pickup-details__section__content">
                <PickupDetailsLog
                  pickupId={pickup.pickupId}
                  onRescheduled={addRescheduleEntry}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {pickup && (
        <>
          <EmailViewerSidebar
            messageId={pickupEmailData?.inboundMessageId ?? null}
            show={showEmailViewer && canViewPickupEmail}
            onClose={() => setShowEmailViewer(false)}
            metadata={
              pickupEmailData?.email
                ? {
                    sender: pickupEmailData.email.sender,
                    receiver: pickupEmailData.email.receiver,
                    subject: pickupEmailData.email.subject,
                    receivedAt: pickupEmailData.email.receivedAt,
                  }
                : undefined
            }
            webLink={pickupEmailData?.email?.webLink}
          />
          <PickupReschedule
            show={showReschedulePickup}
            pickupId={pickup.pickupId}
            pickupNumber={pickup.pickupNumber}
            shipperZip={pickup.shipper.address.postalCode}
            shipperAccountNumber={pickup.shipper.accountNumber}
            onReschedule={(date, readyTime, closeTime) => {
              onReschedulePickup?.({ date, readyTime, closeTime, status: PickupStatus.UNASSIGNED });
              dispatch(getPickupLog(pickup.pickupId));
              setShowReschedulePickup(false);
            }}
            onClose={() => setShowReschedulePickup(false)}
            type="EMPLOYEE"
          />
          <PickupCancel
            show={showCancelPickup}
            pickupId={pickup.pickupId}
            pickupNumber={pickup.pickupNumber}
            shipper={pickup.shipper}
            onCancel={() => {
              onUpdatePickup?.({
                pickupId: pickup.pickupId,
                status: PickupStatus.CANCELED,
                username: userState.profile?.email
              });
              dispatch(getPickupLog(pickup.pickupId));
              setShowCancelPickup(false);
            }}
            onClose={() => setShowCancelPickup(false)}
            type="EMPLOYEE"
          />
          <PickupComplete
            show={showCompletePickup}
            pickupId={pickup.pickupId}
            pickupNumber={pickup.pickupNumber}
            onComplete={() => {
              onUpdatePickup?.({
                pickupId: pickup.pickupId,
                status: PickupStatus.COMPLETED,
                username: userState.profile?.email,
                driverId: pickup.driverId,
                manifestNumber: pickup.manifestNumber
              });
              dispatch(getPickupLog(pickup.pickupId));
              setShowCompletePickup(false);
            }}
            onClose={() => setShowCompletePickup(false)}
          />
        </>
      )}
    </SlideOutSidebar>
  );
};

export default PickupDetails;
